import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useLocation } from 'react-router-dom';
import { PageSection, StatTile, EmptyState } from '@radiology/ui';
import { Box1 } from 'iconsax-react';
import { CERTIFICATIONS_SCREENS } from '../modules/certifications/routes';
import { PURCHASING_SCREENS } from '../modules/purchasing/routes';
import { DEVICES_SCREENS } from '../modules/devices/routes';
import { ADMIN_SCREENS } from '../modules/admin/routes';

/** Screens that have been built. Modules fill this in as their phase lands. */
const BUILT: Record<string, Record<string, Record<string, React.ComponentType>>> = {
  certifications: CERTIFICATIONS_SCREENS,
  purchasing: PURCHASING_SCREENS,
  devices: DEVICES_SCREENS,
};

const PHASE_BY_MODULE: Record<string, string> = {
  certifications: 'Phase 4',
  purchasing: 'Phase 5',
  attendance: 'Phase 6',
  devices: 'Phase 7',
};

/**
 * Stands in for every module screen until that module's phase builds it.
 *
 * It is not filler: it exercises the shell end to end — routing, active nav
 * state, breadcrumbs, the role and module switchers — and it shows the real
 * design system components each screen will be assembled from, so the visual
 * language can be reviewed and signed off before four modules are built on it.
 */
export function PlaceholderPage() {
  const location = useLocation();
  const [, module, role, section] = location.pathname.split('/');
  const phase = PHASE_BY_MODULE[module ?? ''] ?? 'a later phase';

  // A real screen takes precedence over the placeholder.
  const Built = BUILT[module ?? '']?.[role ?? '']?.[section ?? ''];
  if (Built) return <Built />;

  // The administration screens are shared: one User Management, one System
  // Settings and one Audit Log serve every module (Phase 8), so they are matched
  // on the section alone rather than per module.
  if (role === 'admin') {
    const Shared = ADMIN_SCREENS[section ?? ''];
    if (Shared) return <Shared />;
  }

  return (
    <Stack gap={2.5}>
      <Alert severity="info" variant="outlined">
        <AlertTitle>Not built yet</AlertTitle>
        This screen is delivered in <strong>{phase}</strong>. The shell around it — navigation, role
        and module switching, theming — is finished and is what this page is here to demonstrate.
      </Alert>

      <Grid container spacing={2.5}>
        {[
          { label: 'Example metric', value: '—', hint: 'Wired up with real data later' },
          { label: 'Another metric', value: '—', hint: 'Same' },
          { label: 'A third', value: '—', hint: 'Same' },
          { label: 'And a fourth', value: '—', hint: 'Same' },
        ].map(tile => (
          <Grid key={tile.label} size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatTile {...tile} />
          </Grid>
        ))}
      </Grid>

      <PageSection
        title="Route"
        description="Resolved from the central navigation map in src/app/navigation.ts"
      >
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Chip size="small" label={`module: ${module ?? '—'}`} />
          <Chip size="small" label={`role: ${role ?? '—'}`} />
          <Chip size="small" label={`section: ${section ?? '—'}`} />
          <Chip
            size="small"
            variant="outlined"
            label={location.pathname}
            sx={{ fontFamily: 'monospace' }}
          />
        </Stack>
      </PageSection>

      <PageSection title="Content area">
        <EmptyState
          icon={<Box1 size={24} color="currentColor" />}
          title="No data yet"
          description="Real content arrives when this module is migrated. This is the shared empty state every module uses, so 'nothing here' looks the same everywhere."
        />
      </PageSection>

      <Box>
        <Typography variant="caption" color="text.secondary">
          Rendered by PlaceholderPage — replaced screen by screen during the module phases.
        </Typography>
      </Box>
    </Stack>
  );
}
